<template>
  <div>
    <div
      v-if="showBanner"
      id="kal-cookie-banner"
      class="kal-visible"
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies de Kalendario"
    >
      <h2 class="kal-cookie-title">Usamos cookies</h2>
      <p class="kal-cookie-text">
        Kalendario utiliza cookies técnicas necesarias para funcionar y, con tu consentimiento,
        cookies analíticas para mejorar el servicio. Puedes aceptar, rechazar o configurar las cookies no necesarias.
        Consulta nuestra <router-link to="/cookies-policy">Política de Cookies</router-link>.
      </p>
      <div class="kal-cookie-actions">
        <button class="kal-cookie-button kal-cookie-button-primary" type="button" @click="acceptAll">
          Aceptar todas
        </button>
        <button class="kal-cookie-button" type="button" @click="rejectAll">
          Rechazar no necesarias
        </button>
        <button class="kal-cookie-button" type="button" @click="openPanel">
          Configurar
        </button>
      </div>
    </div>

    <div
      v-if="showPanel"
      id="kal-cookie-panel"
      class="kal-visible"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kal-cookie-panel-title"
    >
      <div class="kal-cookie-modal">
        <h2 id="kal-cookie-panel-title" class="kal-cookie-title">Configuración de cookies</h2>
        <p class="kal-cookie-text">
          Puedes elegir qué cookies no necesarias permites. Las cookies técnicas son imprescindibles para el funcionamiento de Kalendario.
        </p>

        <div class="kal-cookie-option">
          <div class="kal-cookie-option-header">
            <h3>Cookies técnicas o necesarias</h3>
            <input class="kal-cookie-switch" type="checkbox" checked disabled aria-label="Cookies técnicas siempre activas">
          </div>
          <p>Necesarias para iniciar sesión, mantener la sesión activa, proteger la cuenta y permitir el funcionamiento básico del servicio.</p>
        </div>

        <div class="kal-cookie-option">
          <div class="kal-cookie-option-header">
            <h3>Cookies de preferencias</h3>
            <input v-model="draft.preferences" class="kal-cookie-switch" type="checkbox" aria-label="Permitir cookies de preferencias">
          </div>
          <p>Permiten recordar ajustes básicos como idioma, configuración visual u otras preferencias simples.</p>
        </div>

        <div class="kal-cookie-option">
          <div class="kal-cookie-option-header">
            <h3>Cookies analíticas</h3>
            <input v-model="draft.analytics" class="kal-cookie-switch" type="checkbox" aria-label="Permitir cookies analíticas">
          </div>
          <p>Nos ayudan a entender el uso de Kalendario y mejorar el servicio mediante métricas agregadas, por ejemplo con Firebase Analytics.</p>
        </div>

        <div class="kal-cookie-actions">
          <button class="kal-cookie-button kal-cookie-button-primary" type="button" @click="saveSettings">
            Guardar configuración
          </button>
          <button class="kal-cookie-button" type="button" @click="acceptAll">
            Aceptar todas
          </button>
          <button class="kal-cookie-button" type="button" @click="rejectAll">
            Rechazar no necesarias
          </button>
          <button class="kal-cookie-button" type="button" @click="closePanel">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { saveCookieConsent } from "@/services/firebase";

const CONSENT_KEY = "kalendario_cookie_consent_v1";
const CONSENT_VERSION = "1.0";

function readConsent() {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value ? JSON.parse(value) : null;
  } catch (_error) {
    return null;
  }
}

export default {
  name: "CookieConsentBanner",
  data: () => ({
    showBanner: false,
    showPanel: false,
    draft: {
      preferences: false,
      analytics: false
    }
  }),
  computed: {
    ...mapGetters("auth", ["uid"])
  },
  watch: {
    uid(nextUid) {
      if (nextUid) {
        this.persistStoredConsentForUser();
      }
    }
  },
  mounted() {
    window.kalendarioResetCookieConsent = () => {
      localStorage.removeItem(CONSENT_KEY);
      this.showBanner = true;
      this.showPanel = false;
    };

    const existingConsent = readConsent();
    if (existingConsent) {
      this.applyConsent(existingConsent);
      this.persistStoredConsentForUser();
    } else {
      this.showBanner = true;
    }
  },
  methods: {
    acceptAll() {
      this.saveConsent({ preferences: true, analytics: true });
    },
    rejectAll() {
      this.saveConsent({ preferences: false, analytics: false });
    },
    openPanel() {
      const consent = readConsent();
      this.draft = {
        preferences: consent ? Boolean(consent.preferences) : false,
        analytics: consent ? Boolean(consent.analytics) : false
      };
      this.showPanel = true;
    },
    closePanel() {
      this.showPanel = false;
    },
    saveSettings() {
      this.saveConsent({
        preferences: this.draft.preferences,
        analytics: this.draft.analytics
      });
    },
    saveConsent(options) {
      const consent = {
        necessary: true,
        preferences: Boolean(options.preferences),
        analytics: Boolean(options.analytics),
        date: new Date().toISOString(),
        version: CONSENT_VERSION
      };

      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
      this.applyConsent(consent);
      this.showBanner = false;
      this.showPanel = false;
      this.persistConsentForUser(consent);
    },
    applyConsent(consent) {
      if (!consent) return;

      if (consent.analytics && typeof window.kalendarioEnableAnalytics === "function") {
        window.kalendarioEnableAnalytics();
      }

      if (consent.preferences && typeof window.kalendarioEnablePreferences === "function") {
        window.kalendarioEnablePreferences();
      }
    },
    persistStoredConsentForUser() {
      const consent = readConsent();
      if (consent) {
        this.persistConsentForUser(consent);
      }
    },
    async persistConsentForUser(consent) {
      if (!this.uid) return;
      try {
        await saveCookieConsent(this.uid, consent);
      } catch (error) {
        console.error("Cookie consent save error:", error);
      }
    }
  }
};
</script>

<style scoped>
#kal-cookie-banner,
#kal-cookie-panel {
  --kal-cookie-bg: #ffffff;
  --kal-cookie-text: #1f2937;
  --kal-cookie-muted: #6b7280;
  --kal-cookie-border: #d1d5db;
  --kal-cookie-primary: #2563eb;
  --kal-cookie-primary-hover: #1d4ed8;
  --kal-cookie-light: #f3f4f6;
  --kal-cookie-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
  color: var(--kal-cookie-text);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

#kal-cookie-banner {
  position: fixed;
  right: 16px;
  bottom: 16px;
  left: 16px;
  z-index: 9999;
  max-width: 980px;
  margin: 0 auto;
  padding: 18px;
  border: 1px solid var(--kal-cookie-border);
  border-radius: 14px;
  background: var(--kal-cookie-bg);
  box-shadow: var(--kal-cookie-shadow);
}

.kal-cookie-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
}

.kal-cookie-text {
  margin: 0;
  color: var(--kal-cookie-muted);
  font-size: 14px;
  line-height: 1.45;
}

.kal-cookie-text a {
  color: var(--kal-cookie-primary);
  text-decoration: underline;
}

.kal-cookie-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.kal-cookie-button {
  border: 1px solid var(--kal-cookie-border);
  border-radius: 999px;
  background: #ffffff;
  color: var(--kal-cookie-text);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 15px;
}

.kal-cookie-button:hover {
  background: var(--kal-cookie-light);
}

.kal-cookie-button-primary {
  border-color: var(--kal-cookie-primary);
  background: var(--kal-cookie-primary);
  color: #ffffff;
}

.kal-cookie-button-primary:hover {
  background: var(--kal-cookie-primary-hover);
}

#kal-cookie-panel {
  position: fixed;
  z-index: 10000;
  inset: 0;
  overflow: auto;
  background: rgba(17, 24, 39, 0.56);
  padding: 18px;
}

.kal-cookie-modal {
  max-width: 720px;
  margin: 48px auto;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: var(--kal-cookie-shadow);
  padding: 22px;
}

.kal-cookie-option {
  margin-top: 12px;
  border: 1px solid var(--kal-cookie-border);
  border-radius: 12px;
  padding: 14px;
}

.kal-cookie-option-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.kal-cookie-option h3 {
  margin: 0;
  font-size: 15px;
}

.kal-cookie-option p {
  margin: 8px 0 0;
  color: var(--kal-cookie-muted);
  font-size: 13px;
  line-height: 1.45;
}

.kal-cookie-switch {
  width: 18px;
  height: 18px;
  accent-color: var(--kal-cookie-primary);
}

@media (max-width: 640px) {
  #kal-cookie-banner {
    right: 10px;
    bottom: 10px;
    left: 10px;
    padding: 14px;
  }

  .kal-cookie-actions {
    flex-direction: column;
  }

  .kal-cookie-button {
    width: 100%;
  }
}
</style>
